import { useColorScheme } from '@mui/material'
import { useNavigate } from '@tanstack/react-router'
import maplibregl from 'maplibre-gl'
import { useContext, useEffect, useRef } from 'react'
import { MapRenderContext } from 'src/contexts/MapRenderContext'
import { useListStopsStopsGet } from 'src/generated/brezavta/queries'
import { Stop } from 'src/generated/brezavta/requests/types.gen'
import { ICON_BASE_SIZE } from 'src/lib/constants'
import { addRandomError, namespaceIconExpression, useSimpleCache } from 'src/lib/util'

const ICON_MIN_ZOOM = 11

const bindStopInteractions = (map: maplibregl.Map, symbolLayerId: string, navigate, cleanups: (() => void)[]) => {
    if (!map.getLayer(symbolLayerId)) return

    const enterHandler = () => {
        map.getCanvas().style.cursor = 'pointer'
    }
    const leaveHandler = () => {
        map.getCanvas().style.cursor = ''
    }
    const clickHandler = (e: maplibregl.MapLayerMouseEvent) => {
        const id = e.features?.[0]?.properties?.id
        if (!id) return
        navigate({ to: `/stop/${id}` })
    }

    map.on('mouseenter', symbolLayerId, enterHandler)
    map.on('mouseleave', symbolLayerId, leaveHandler)
    map.on('click', symbolLayerId, clickHandler)

    cleanups.push(() => {
        map.off('mouseenter', symbolLayerId, enterHandler)
        map.off('mouseleave', symbolLayerId, leaveHandler)
        map.off('click', symbolLayerId, clickHandler)
    })
}

const setData = (map: maplibregl.Map, stops: Stop[], agency: string, navigate, isDark: boolean, cleanups: (() => void)[]) => {
    const circleLayerId = `stops-circles-${agency.toLowerCase()}`
    const symbolLayerId = `stops-symbol-${agency.toLowerCase()}`

    if (!map.getSource(circleLayerId)) {
        map.addSource(circleLayerId, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [],
            },
        })
    }

    if (!map.getLayer(circleLayerId)) {
        map.addLayer({
            id: circleLayerId,
            type: 'circle',
            source: circleLayerId,
            maxzoom: ICON_MIN_ZOOM,
            paint: {
                'circle-radius': [
                    'interpolate', ['linear'],
                    ['zoom'],
                    1, 1,
                    5, 3,
                    ICON_MIN_ZOOM, 2,
                ],
                'circle-color': ['get', 'background_color'],
                'circle-opacity': ['step', ['zoom'], 0, 7, 0.14, 7.5, 0.25, 7.75, 0.5, 8, 1, 14, 0],
            },
        })
    }

    if (!map.getLayer(symbolLayerId)) {
        map.addLayer({
            id: symbolLayerId,
            type: 'symbol',
            source: circleLayerId,
            minzoom: ICON_MIN_ZOOM,
            layout: {
                'icon-image': namespaceIconExpression(['get', 'icon']),
                'icon-size': [
                    'interpolate', ['linear'],
                    ['zoom'],
                    10, ICON_BASE_SIZE * 0.1,
                    13, ICON_BASE_SIZE * 1,
                ],
                'icon-allow-overlap': true,
                'icon-anchor': 'center',
                'text-field': '{name}',
                'text-anchor': 'top',
                'text-offset': [0, 1],
                'text-size': ['step', ['zoom'], 0, 16, 12],
                'text-font': ['Inter Italic'],
                'text-padding': 1,
                'text-max-width': 8,
                'text-allow-overlap': true,
            },
            paint: {
                'text-color': isDark ? ['coalesce', ['get', 'background_color'], '#aaaaaa'] : ['get', 'background_color'],
                'icon-opacity': 1,
                'text-opacity': 1,
                'text-halo-blur': 0.5,
                'text-halo-color': isDark ? '#646464' : '#fff',
                'text-halo-width': 3,
            },
        })

    }

    bindStopInteractions(map, symbolLayerId, navigate, cleanups)

    map.getSource(circleLayerId).setData({
        type: 'FeatureCollection',
        features: stops.map((stop) => {
            const { lat, lon, name, gtfs_id, type, background_color, icon_color } = stop
            const adjustedLat = addRandomError(lat)
            const adjustedLon = addRandomError(lon)
            return {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [adjustedLon, adjustedLat],
                },
                properties: {
                    name,
                    agency,
                    type,
                    id: gtfs_id,
                    background_color,
                    icon_color,
                    icon: `${agency.toLowerCase()}_${type.toLowerCase()}`,
                },
            }
        }),
    })
}

const StopLayer = () => {
    const { map, mapLoaded } = useContext(MapRenderContext)
    const navigate = useNavigate()
    const { mode } = useColorScheme()
    const isDark = mode === 'dark'
    const cleanupsRef = useRef<(() => void)[]>([])

    const { data } = useListStopsStopsGet()
    const stops = useSimpleCache(data, 'stops', [])

    useEffect(() => {
        if (!map || !map.current || !mapLoaded) return

        const uniqueAgencyIDs = ['LPP', 'IJPP', 'MARPROM', 'CELEBUS', 'MOKRANJ', 'MOKRSKO', 'MONM', 'BREZICE', 'SEVNICA', 'MOVELENJE', 'MOMS', 'POSTOJNA']

        for (const agency of uniqueAgencyIDs) {
            const agencyStops = stops.filter(
                stop => stop.gtfs_id.split(':')[0].toUpperCase() === agency,
            )
            setData(map.current, agencyStops, agency.toLowerCase(), navigate, isDark, cleanupsRef.current)
        }

        const otherStops = stops.filter(
            stop => !uniqueAgencyIDs.includes(stop.gtfs_id.split(':')[0].toUpperCase()),
        )
        setData(map.current, otherStops, 'unknown', navigate, isDark, cleanupsRef.current)

        return () => {
            cleanupsRef.current.forEach(fn => fn())
            cleanupsRef.current = []
        }
    }, [map, mapLoaded, stops, navigate, isDark])

    return null
}

export default StopLayer
